import { CognitoIdToken } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';

const MAINTENANCE_BANNER_DISMISSAL_KEY = 'maintenanceBannerDismissal';

/**
 * Reads auth_time of the current sign-in. Prefers the ID token the ERP iframe stores in localStorage, because after an ERP sign-out and SSO sign-in as the same user Amplify keeps this portal's old session and its old auth_time. A direct sign-in stores none, so it falls back to the session in the shared Amplify cookies.
 *
 * @returns A promise resolving to the ID token's auth_time.
 */
const getAuthTime = async () =>
  localStorage.idToken
    ? new CognitoIdToken({ IdToken: localStorage.idToken }).payload.auth_time
    : (await Auth.currentSession()).getIdToken().payload.auth_time;

// auth_time only changes on a new sign-in (not on token refresh), so it also covers sign-outs from
// other portals that don't clear this portal's localStorage.
/**
 * Captures what identifies a dismissal of the maintenance banner: the current local day and the current sign-in. Reads auth_time through getAuthTime and throws when the token carries none, so callers fall back to showing the banner.
 *
 * @returns A promise resolving to the local date as YYYY-MM-DD and the ID token's auth_time.
 */
const getMaintenanceBannerDismissal = async () => {
  const authTime = await getAuthTime();
  // A missing auth_time would compare undefined === undefined and keep the banner hidden.
  if (authTime === undefined) {
    throw new Error('ID token has no auth_time');
  }
  return { date: dayjs().format('YYYY-MM-DD'), authTime };
};

// Fails open: showing the maintenance notice again beats hiding it when the session can't be read.
/**
 * Decides whether the maintenance banner stays hidden because it was already closed. Compares the dismissal stored in localStorage with the current local day and auth_time, and returns false after logging when the stored value or the session cannot be read.
 *
 * @returns A promise resolving to true only when the stored dismissal matches today and the current sign-in.
 */
export const isMaintenanceBannerDismissed = async () => {
  try {
    const dismissal = JSON.parse(localStorage.getItem(MAINTENANCE_BANNER_DISMISSAL_KEY) || 'null');
    const current = await getMaintenanceBannerDismissal();
    return dismissal?.date === current.date && dismissal?.authTime === current.authTime;
  } catch (error) {
    console.error('Failed to check maintenance banner dismissal:', error);
    return false;
  }
};

/**
 * Records that the user closed the maintenance banner for the current day and sign-in. Writes the current dismissal to localStorage and only logs the error when the session cannot be read, so the banner reappears on the next load.
 *
 * @returns A promise resolving once the dismissal has been stored or the failure logged.
 */
export const dismissMaintenanceBanner = async () => {
  try {
    localStorage.setItem(
      MAINTENANCE_BANNER_DISMISSAL_KEY,
      JSON.stringify(await getMaintenanceBannerDismissal()),
    );
  } catch (error) {
    console.error('Failed to save maintenance banner dismissal:', error);
  }
};
